import React, { ReactElement, useEffect, useState, useCallback, useMemo } from "react";
import classNames from "classnames";
import { QualityHubTasksListItem } from "../QualityHubTasksListItem";
import {
    QualityHubTasksListGroupProps,
} from "./QualityHubTasksListGroup.types";
import { MediumBodyBolder, CaptionBolder, CaptionBody } from "src/components/Text";
import Visage2Icon from "src/visage2/Visage2Icon/Visage2Icon";
import { UnstyledButton } from "src/ui";
import { Conditional, SmallBodyBolder } from "src/components";
import { SpintrTypes } from "src/typings";
import { isSameDate } from "src/utils";
import { localize } from "src/l10n";
import { Droppable, Draggable } from "react-beautiful-dnd";
import { getFilteredTasksForGroup } from "../QualityHubTasksList.utils";

function isOverdue(
    group: Spintr.TaskListGroup,
    listMode?: Spintr.QualityHubTasksListMode | undefined,
): boolean {
    if (listMode === SpintrTypes.QualityHubTasksListMode.Customization ||
        listMode === SpintrTypes.QualityHubTasksListMode.Creation) {
        return false; // In edit mode, do not mark as overdue
    }

    if (!group.endTime) {
        return false; // No time constraints, not overdue
    }

    const endTime = new Date();
    const [hours, minutes] = group.endTime.split(":").map(Number);
    endTime.setHours(hours, minutes, 0, 0);

    return endTime < new Date(); // Overdue if current time is past end time
}

/**
 * Determines if a group should be collapsed based on its properties.
 *
 * @param {QualityHubTasksListGroupProps["group"]} group - The group to evaluate
 * @returns {boolean} True if the group should be collapsed, false otherwise
 */
function shouldBeCollapsed(
    group: QualityHubTasksListGroupProps["group"],
    taskList: QualityHubTasksListGroupProps["taskList"],
    expandedItemId?: number | undefined,
    listMode?: Spintr.QualityHubTasksListMode | undefined,
): boolean {
    // A group should be collapsed if:
    // 1. It has no tasks
    // 2. All tasks are completed or failed
    // 3. The start and/or end time is not in the current time range

    if (listMode === SpintrTypes.QualityHubTasksListMode.Customization ||
        listMode === SpintrTypes.QualityHubTasksListMode.Creation) {
        return false; // In edit mode, do not collapse groups
    }
    
    const tasks = (taskList.tasks ?? [])
        .filter((task) => task.groupId === group.id);

    if (tasks.length === 0) {
        return true;
    }

    if (expandedItemId && tasks.some((task) => task.id === expandedItemId)) {
        return false; // If any task is expanded, do not collapse
    }

    const now = new Date();
    const incompleteTasks = tasks.filter(
        (task) => (taskList.statuses ?? []).every(
            (status) => status.itemId !== task.id
                     || (status.status !== SpintrTypes.TaskListItemStatus.Completed
                      && status.status !== SpintrTypes.TaskListItemStatus.Failed)
                     || !isSameDate(now, status.reportedOn),
        ),
    );

    if (incompleteTasks.length === 0) {
        return true; // All tasks are completed or failed
    }

    if (!group.startTime && !group.endTime) {
        return false; // No time constraints, do not collapse
    }

    if (group.startTime) {
        const startTime = new Date();
        const [hours, minutes] = group.startTime.split(":").map(Number);
        startTime.setHours(hours, minutes, 0, 0);

        if (startTime > now) {
            return true; // Start time is in the future, collapse
        }
    }

    if (isOverdue(group)) {
        return true; // Group is overdue, collapse
    }

    // If we reach here, the group is not overdue and has incomplete tasks
    // so it should not be collapsed
    return false;
}

/**
 * Renders a group of tasks in the QualityHubTasksList component.
 *
 * @param {QualityHubTasksListGroupProps} props - The component props
 * @returns {ReactElement | null} The rendered component
 */
function QualityHubTasksListGroup({
    expandedItemId,
    group,
    onCreateActionClick,
    onEditGroupClick,
    onItemClick,
    tasks: passedTasks,
    taskList,
    listMode,
    hideDisabledItems,
    pendingTaskChanges,
    onTaskChange,
    onCreateTaskClick,
    isAnyItemDragging = false
}: QualityHubTasksListGroupProps): ReactElement | null {
    const [isCollapsed, setIsCollapsed] = useState(
        () => shouldBeCollapsed(group, taskList, expandedItemId, listMode),
    );

    const className = useMemo(() => classNames("QualityHubTasksListGroup", {
        "collapsed": isCollapsed,
    }), [isCollapsed]);

    const toggleCollapsed = useCallback(
        () => setIsCollapsed((prev) => !prev),
        [setIsCollapsed]);

    const onHeaderClicked = useCallback(() => {
        if (listMode !== SpintrTypes.QualityHubTasksListMode.Creation &&
            listMode !== SpintrTypes.QualityHubTasksListMode.Customization) {
            toggleCollapsed();
            return;
        }

        if (onEditGroupClick) {
            onEditGroupClick(group);
        }
    }, [toggleCollapsed, listMode, onEditGroupClick, group]);

    useEffect(function _QualityHubTasksListGroupMount() {
        const intervalId = setInterval(function _qualityHubTasksListGroupUpdate() {
            setIsCollapsed(shouldBeCollapsed(group, taskList, expandedItemId, listMode));
        }, 1000 * 60); // Check every minute

        return function _QualityHubTasksListGroupUnmount() {
            clearInterval(intervalId);
        };
    }, [group, taskList, setIsCollapsed, expandedItemId, listMode]);

    useEffect(() => {
        setIsCollapsed(shouldBeCollapsed(group, taskList, expandedItemId, listMode));
    }, [expandedItemId, group, taskList, listMode]);

    const tasks = useMemo(() => {
        if (passedTasks) {
            return passedTasks;
        }

        // Get filtered tasks for this group
        let filteredTasks = getFilteredTasksForGroup(
            group.id,
            taskList,
            { groups: new Map(), tasks: pendingTaskChanges ?? new Map() },
            hideDisabledItems,
            listMode);

        if (listMode === SpintrTypes.QualityHubTasksListMode.Creation) {
            filteredTasks = filteredTasks
                .filter((t) => !t.forUberId);
        }

        if (isCollapsed) {
            // If it is collapsed and in edit mode, hide all tasks
            if (listMode) {
                return [];
            }
            // If the group is collapsed, only show tasks that are overdue
            filteredTasks = filteredTasks.filter((task) => {
                const taskStatus = (taskList.statuses ?? []).find(
                    (status) => status.itemId === task.id,
                );

                return isOverdue(group) && (!taskStatus
                    || (taskStatus.status !== SpintrTypes.TaskListItemStatus.Completed
                     && taskStatus.status !== SpintrTypes.TaskListItemStatus.Failed));
            });
        }

        return filteredTasks;
    }, [passedTasks, isCollapsed, taskList, group.id, listMode, hideDisabledItems, pendingTaskChanges]);

    const renderItem = useCallback((item: Spintr.TaskListTask, index: number) => {
        const overdue = isOverdue(group, listMode);
        const isCompleted = (taskList.statuses ?? []).some(
            (status) => status.itemId === item.id
                     && (status.status === SpintrTypes.TaskListItemStatus.Completed
                      || status.status === SpintrTypes.TaskListItemStatus.Failed),
        );

        const className = classNames("QualityHubTasksListGroup-item", {
            "expanded": expandedItemId === item.id,
        });

        const isEditing =
            listMode === SpintrTypes.QualityHubTasksListMode.Creation ||
            listMode === SpintrTypes.QualityHubTasksListMode.Customization;

        if (isEditing) {
            return (
                <Draggable key={item.id} draggableId={item.id.toString()} index={index}>
                    {(provided, snapshot) => (
                        <li
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            className={`${className} ${snapshot.isDragging ? 'isDragging' : ''}`}
                        >
                            <QualityHubTasksListItem
                                isExpanded={expandedItemId === item.id}
                                item={item}
                                onCreateActionClick={onCreateActionClick}
                                onItemClick={onItemClick}
                                overdue={overdue && !isCompleted}
                                listMode={listMode}
                                pendingChanges={pendingTaskChanges?.get(item.id)}
                                onTaskChange={onTaskChange}
                                dragHandleProps={provided.dragHandleProps}
                                isDragging={snapshot.isDragging}
                                isAnyItemDragging={isAnyItemDragging}
                            />
                        </li>
                    )}
                </Draggable>
            );
        }

        return (
            <li key={item.id} className={className}>
                <QualityHubTasksListItem
                    isExpanded={expandedItemId === item.id}
                    item={item}
                    onCreateActionClick={onCreateActionClick}
                    onItemClick={onItemClick}
                    overdue={overdue && !isCompleted}
                    listMode={listMode}
                    pendingChanges={pendingTaskChanges?.get(item.id)}
                    onTaskChange={onTaskChange}
                    isDragging={false}
                    isAnyItemDragging={isAnyItemDragging}
                />
            </li>
        );
    }, [group, taskList, expandedItemId, listMode, pendingTaskChanges, onTaskChange, isAnyItemDragging]);

    const isEditing =
        listMode === SpintrTypes.QualityHubTasksListMode.Creation ||
        listMode === SpintrTypes.QualityHubTasksListMode.Customization;

    const addNewTaskButton = (!isEditing || !onCreateTaskClick) ? undefined : (
        <UnstyledButton className="QualityHubTasksListGroup-addTask"
                        onClick={onCreateTaskClick}
                        onClickData={group.id}>
            <Visage2Icon icon="add" />
            <SmallBodyBolder>
                {localize("ADD_TASK")}
            </SmallBodyBolder>
        </UnstyledButton>
    );

    return (
        <div className={className}>
            <UnstyledButton className="QualityHubTasksListGroup-header"
                            onClick={onHeaderClicked}>
                <div className="QualityHubTasksListGroup-headerLeft">
                    <MediumBodyBolder className="QualityHubTasksListGroup-title"
                                      color="contentDark">
                        {group.name}
                    </MediumBodyBolder>
                    <Conditional condition={group.startTime || group.endTime}>
                        <CaptionBody className="QualityHubTasksListGroup-timeLabel">
                            {(group.startTime || "").substr(0, 5)}
                            &nbsp;-&nbsp;
                            {(group.endTime || "").substr(0, 5)}
                        </CaptionBody>
                    </Conditional>
                </div>
                <Conditional condition={group.keyConsideration}>
                    <CaptionBolder className="QualityHubTasksListGroup-headerRight">
                        {group.keyConsideration}
                    </CaptionBolder>
                </Conditional>
                <Conditional condition={listMode === SpintrTypes.QualityHubTasksListMode.Execution}>
                    <Visage2Icon className="QualityHubTasksListGroup-collapseIcon"
                                 color="spintrGrey"
                                 size="medium"
                                 icon={isCollapsed ? "arrow-down-1" : "arrow-up-2"} />
                </Conditional>

            </UnstyledButton>
            <Conditional condition={tasks.length > 0}>
                <Conditional condition={isEditing}>
                    <Droppable droppableId={group.id.toString()}>
                        {(provided, _) => (
                            <ol
                                className="QualityHubTasksListGroup-items"
                                ref={provided.innerRef}
                                {...provided.droppableProps}
                            >
                                {tasks.map((task, index) => renderItem(task, index))}
                                {provided.placeholder}
                                <Conditional condition={addNewTaskButton}>
                                    <li className="QualityHubTasksListGroup-item">
                                        {addNewTaskButton}
                                    </li>
                                </Conditional>
                            </ol>
                        )}
                    </Droppable>
                </Conditional>
                <Conditional condition={!isEditing}>
                    <ol className="QualityHubTasksListGroup-items">
                        {tasks.map((task, index) => renderItem(task, index))}
                    </ol>
                </Conditional>
            </Conditional>
            <Conditional condition={tasks.length === 0 && isEditing && addNewTaskButton}>
                <div className="QualityHubTasksListGroup-noItems">
                    {addNewTaskButton}
                </div>
            </Conditional>
        </div>
    );
}

export default QualityHubTasksListGroup;

